import type { Fighter } from './types';

export type EnemyDef = {
  id: string;
  name: string;
  hp: number;
  sp: number;
  armor: number;
  // Optional: relative chance to be picked by pickRandomEnemyId (default 1).
  spawnWeight?: number;
  // Optional: define a specific enemy deck by card IDs (and optional display titles).
  // If omitted, the enemy will use the full loaded enemy card pool.
  enemyCards?: Array<{ id: string; title?: string }>;
  allowEnemyLibraryFallbackWhenEmpty?: boolean;
};

export type RuntimeEnemyOverride = {
  hp?: number;
  armor?: number;
  sp?: number;
  hpMultiplier?: number;
  armorMultiplier?: number;
  spMultiplier?: number;
};

const runtimeEnemyOverrides = new Map<string, RuntimeEnemyOverride>();

function applyRuntimeEnemyOverride(def: EnemyDef, override?: RuntimeEnemyOverride | null): EnemyDef {
  if (!override) return def;

  const hp = override.hp != null
    ? Math.max(1, Math.floor(Number(override.hp) || def.hp))
    : Math.max(1, Math.floor(def.hp * Math.max(0, Number(override.hpMultiplier ?? 1) || 1)));

  const armor = override.armor != null
    ? Math.max(0, Math.floor(Number(override.armor) || def.armor))
    : Math.max(0, Math.floor(def.armor * Math.max(0, Number(override.armorMultiplier ?? 1) || 1)));

  const sp = override.sp != null
    ? Math.max(0, Math.floor(Number(override.sp) || def.sp))
    : Math.max(0, Math.floor(def.sp * Math.max(0, Number(override.spMultiplier ?? 1) || 1)));

  return {
    ...def,
    hp,
    armor,
    sp,
  };
}

export function setRuntimeEnemyOverride(idOrName: unknown, override: RuntimeEnemyOverride | null) {
  const found = findEnemyDef(idOrName);
  if (!found) return;

  if (!override) {
    runtimeEnemyOverrides.delete(found.id);
    return;
  }

  runtimeEnemyOverrides.set(found.id, { ...override });
}

export function clearAllRuntimeEnemyOverrides() {
  runtimeEnemyOverrides.clear();
}

// Enemy roster.
// NOTE: For now, all enemies share the same enemy card pool (enemyDeck). When you later
// want different decks per enemy, we can extend this to include an `enemyCardIds` list
// or a `tag` that filters the loaded enemy cards.
export const NESTED_HORROR_PARALYZING_VENOM = {
  cardId: '69add4c8e3b805d4a0daf6f5',
  title: 'Paralyzing Venom',
  firstTickDamage: 0,
  damageStepPerTurn: 2,
} as const;

const CULTIST_ENEMY_CARDS: Array<{ id: string; title?: string }> = [
  { id: '6998d4fa0bebb02c4299bfd4', title: 'Power Strike' },
  { id: '6998d4830bebb02c4299bfd0', title: 'Claw Attack' },
  { id: '6998d4cc0bebb02c4299bfd2', title: 'Sprint' },
  { id: '6998d4550bebb02c4299bfce', title: 'Frontal Slash' },
  { id: '6998d4270bebb02c4299bfcc', title: 'Defensive Stance' },
];

const YAHAKA_ENEMY_CARDS: Array<{ id: string; title?: string }> = [
  { id: '69bac28ec3e5ef3bbff71ced', title: 'Call of the Void' },
  { id: '69bac200c3e5ef3bbff71ce7', title: 'Abyss Claws' },
  { id: '69bac254c3e5ef3bbff71cea', title: 'Crouching Stance' },
  { id: '69bac2ffc3e5ef3bbff71cf0', title: 'Unholy Fangs' },
  { id: '69bac1bcc3e5ef3bbff71ce4', title: 'Thick Fur' },
];

export const YAHAKA_VARIANT_1_ENEMY_CARDS = [...YAHAKA_ENEMY_CARDS];
export const YAHAKA_VARIANT_2_ENEMY_CARDS = [...YAHAKA_ENEMY_CARDS];
export const YAHAKA_VARIANT_3_ENEMY_CARDS = [...YAHAKA_ENEMY_CARDS];
export const YAHAKA_VARIANT_4_ENEMY_CARDS = [...YAHAKA_ENEMY_CARDS];
export const YAHAKA_VARIANT_5_ENEMY_CARDS = [...YAHAKA_ENEMY_CARDS];
export const YAHAKA_VARIANT_6_ENEMY_CARDS = [...YAHAKA_ENEMY_CARDS];
export const YAHAKA_VARIANT_7_ENEMY_CARDS = [...YAHAKA_ENEMY_CARDS];

export const ENEMIES: EnemyDef[] = [
  {
    id: 'cultist',
    name: 'Cultist',
    hp: 100,
    sp: 0,
    armor: 0,
    spawnWeight: 1,
    // Cultist uses a custom set of enemy cards.
    // Replace the IDs/titles below with your real Cultist enemy card IDs.
    // (Repeat an ID multiple times if you want multiple copies to increase its odds.)
    enemyCards: CULTIST_ENEMY_CARDS,
  },
  {
    id: 'corrupted_cultist',
    name: 'Corrupted Cultist',
    hp: 200,
    sp: 0,
    armor: 60,
    // Corruption-only dungeon spawn. Never picked by random combat start.
    spawnWeight: 0,
    // Clone the current Cultist move set for now.
    // Later you can replace this with a custom corrupted deck.
    enemyCards: [...CULTIST_ENEMY_CARDS],
  },
  // Clone of Cultist to start iterating on a new enemy.
  // Feel free to rename + adjust stats/armor to shape difficulty.
  {
    id: 'hexbinder',
    name: 'Hexbinder',
    hp: 140,
    sp: 0,
    armor: 25,
    spawnWeight: 1,
    // Hexbinder uses a custom set of enemy cards.
    // Replace the IDs/titles below with your real Hexbinder enemy card IDs.
    enemyCards: [
      { id: '6998d5cf0bebb02c4299bfe0', title: 'Hex' },
      { id: '6998d5ad0bebb02c4299bfdd', title: 'Bolt' },
      { id: '6998d5870bebb02c4299bfdb', title: 'Meditation' },
      { id: '6998d54d0bebb02c4299bfd9', title: 'Ritual' },
      { id: '6998d5350bebb02c4299bfd6', title: 'Ward' },
    ],
  },

{
    id: 'chymist',
    name: 'Chymist',
    hp: 100,
    sp: 0,
    armor: 0,
    spawnWeight: 1,
    enemyCards: [
      { id: '69ccf311bbea17fa136d85d9', title: 'Caustic Flask' },
      { id: '69ccf311bbea17fa136d85d9', title: 'Caustic Flask' },
      { id: '69ccf311bbea17fa136d85d9', title: 'Caustic Flask' },
      { id: '69ccf390bbea17fa136d85db', title: 'Corrosive Mist' },
      { id: '69ccf390bbea17fa136d85db', title: 'Corrosive Mist' },
      { id: '69ccf3fdbbea17fa136d85e0', title: 'Reinjection' },
      { id: '69ccf49cbbea17fa136d85e2', title: 'Volatile Reaction' },
      { id: '69ccf49cbbea17fa136d85e2', title: 'Volatile Reaction' },

      { id: '69ccf50ebbea17fa136d85e4', title: 'Caustic Compound' },
      { id: '69ccf53cbbea17fa136d85e6', title: 'Mindrot Compound' },
      { id: '69ccf583bbea17fa136d85e8', title: 'Iron Compound' },
      { id: '69ccf5cbbbea17fa136d85ea', title: 'Catalyst Compound' },
      { id: '69ccf606bbea17fa136d85ec', title: 'Sedative Compound' },
    ],
  },

{
    id: 'chained-penitent',
    name: 'Chained Penitent',
    hp: 100,
    sp: 0,
    armor: 0,
    spawnWeight: 1,
    enemyCards: [
      { id: '69c23e5681fa6b75f1a2b27b', title: 'Piercing Attack' },
      { id: '69c23cf781fa6b75f1a2b274', title: 'Self-punishment' },
      { id: '69c23c9581fa6b75f1a2b271', title: 'Chain Armor' },
      { id: '69c23c5581fa6b75f1a2b26e', title: 'Iron Whip' },
    ],
  },

  {
    id: 'monsignor',
    name: 'The Monsignor',
    hp: 666,
    sp: 0,
    armor: 0,
    // Mid-run boss: flip this to 1 when you want random combat selection to include him.
    spawnWeight: 1,
    // Keep this explicit empty list until the real enemy card ids exist.
    enemyCards: [
      { id: '69c398e4dc9b2da89a95be55', title: 'Decree of Restraint' },
      { id: '69c398e4dc9b2da89a95be55', title: 'Decree of Restraint' },
      { id: '69c398e4dc9b2da89a95be55', title: 'Decree of Restraint' },
      { id: '69c39962dc9b2da89a95be58', title: 'Crozier Strike' },
      { id: '69c39a12dc9b2da89a95be62', title: 'Brand of Heresy' },
      { id: '69c39a12dc9b2da89a95be62', title: 'Brand of Heresy' },
      { id: '69c39ab4dc9b2da89a95be6a', title: 'Penance Tax' },
      { id: '69c39b23dc9b2da89a95be6d', title: 'False Absolution' },
      { id: '69c39ba1dc9b2da89a95be74', title: 'Judgment' },
      { id: '69c39c4adc9b2da89a95be7d', title: 'Litany of Shame' },
    ],
    // Prevent the empty list from borrowing the shared enemy library by accident.
    allowEnemyLibraryFallbackWhenEmpty: false,
  },

  {
    id: 'prime_vessel',
    name: 'Prime Vessel',
    hp: 999,
    sp: 0,
    armor: 99,
    // Mid-run boss: flip this to 1 when you want random combat selection to include him.
    spawnWeight: 1,
    // Keep this explicit empty list until the real enemy card ids exist.
    enemyCards: [
      { id: '69ca4812e7d7200184ff0549', title: 'Ruptured Ascension' },
      { id: '69ca4765e7d7200184ff0546', title: 'Protection Protocol' },
      { id: '69ca4765e7d7200184ff0546', title: 'Protection Protocol' },
      { id: '69ca46cae7d7200184ff0544', title: 'Containment Breach' },
      { id: '69ca464de7d7200184ff0542', title: 'Corruption Surge' },
      { id: '69ca464de7d7200184ff0542', title: 'Corruption Surge' },
      { id: '69ca464de7d7200184ff0542', title: 'Corruption Surge' },
    ],
    // Prevent the empty list from borrowing the shared enemy library by accident.
    allowEnemyLibraryFallbackWhenEmpty: false,
  },

{
    id: 'prime_crucible_device',
    name: 'Prime Crucible',
    hp: 99,
    sp: 0,
    armor: 99,
    // Mid-run boss: flip this to 1 when you want random combat selection to include him.
    spawnWeight: 0,
    // Keep this explicit empty list until the real enemy card ids exist.
    enemyCards: [
      { id: '69ccee177ef92c38779fab8b', title: 'Steam Release' },
      { id: '69ccee177ef92c38779fab8b', title: 'Steam Release' },
      { id: '69ccedbb7ef92c38779fab87', title: 'Raged Homunculus' },
      { id: '69ccee927ef92c38779fab8d', title: 'Fortification' },
    ],
    // Prevent the empty list from borrowing the shared enemy library by accident.
    allowEnemyLibraryFallbackWhenEmpty: false,
  },

  {
    id: 'pulse_engine_device',
    name: 'Pulse Engine',
    hp: 99,
    sp: 0,
    armor: 99,
    // Mid-run boss: flip this to 1 when you want random combat selection to include him.
    spawnWeight: 0,
    // Keep this explicit empty list until the real enemy card ids exist.
    enemyCards: [
      { id: '69cba866892c704163c0e8d1', title: 'Steam Release' },
      { id: '69cba866892c704163c0e8d1', title: 'Steam Release' },
      { id: '69cba8cf892c704163c0e8d3', title: 'Energy Field' },
      { id: '69cba93e892c704163c0e8d9', title: 'Overheat' },
    ],
    // Prevent the empty list from borrowing the shared enemy library by accident.
    allowEnemyLibraryFallbackWhenEmpty: false,
  },

{
    id: 'synaptic_retort_device',
    name: 'Synaptic Retort',
    hp: 99,
    sp: 0,
    armor: 99,
    // Mid-run boss: flip this to 1 when you want random combat selection to include him.
    spawnWeight: 0,
    // Keep this explicit empty list until the real enemy card ids exist.
    enemyCards: [
      { id: '69cba980892c704163c0e8db', title: 'Steam Release' },
      { id: '69cba980892c704163c0e8db', title: 'Steam Release' },
      { id: '69cba9e2892c704163c0e8dd', title: 'Self-repair' },
      { id: '69cbaa62892c704163c0e8e2', title: 'Energy Beam' },
    ],
    // Prevent the empty list from borrowing the shared enemy library by accident.
    allowEnemyLibraryFallbackWhenEmpty: false,
  },

{
    id: 'astral_reliquary_device',
    name: 'Astral Reliquary',
    hp: 99,
    sp: 0,
    armor: 99,
    // Mid-run boss: flip this to 1 when you want random combat selection to include him.
    spawnWeight: 0,
    // Keep this explicit empty list until the real enemy card ids exist.
    enemyCards: [
      { id: '69cba76e892c704163c0e8c9', title: 'Steam Release' },
      { id: '69cba76e892c704163c0e8c9', title: 'Steam Release' },
      { id: '69cba727892c704163c0e8c7', title: 'Astral Beam' },
      { id: '69cba7ff892c704163c0e8ce', title: 'Crystal Shards' }, 
    ],
    // Prevent the empty list from borrowing the shared enemy library by accident.
    allowEnemyLibraryFallbackWhenEmpty: false,
  },

{
    id: 'blackhand',
    name: 'Blackhand',
    hp: 470,
    sp: 0,
    armor: 60,
    // Mid-run boss: flip this to 1 when you want random combat selection to include him.
    spawnWeight: 1,
    // Keep this explicit empty list until the real enemy card ids exist.
    enemyCards: [
      { id: '69caf81b816db67213cc7251', title: 'Blackhand Sigil' },
      { id: '69cac01eb67bdde39f741f51', title: 'Momentum' },
      { id: '69cb1f9f5c25f2a37b92fdd3', title: 'Eviscerate' },
      { id: '69cabccbb67bdde39f741f43', title: 'Execute' },
      { id: '69cabc50b67bdde39f741f41', title: 'Pressure Point' },
      { id: '69cabb6bb67bdde39f741f3f', title: 'Shadowstep' },
    ],
    // Prevent the empty list from borrowing the shared enemy library by accident.
    allowEnemyLibraryFallbackWhenEmpty: false,
  },

{
    id: 'mercenary',
    name: 'Mercenary',
    hp: 120,
    sp: 0,
    armor: 30,
    spawnWeight: 1,
    enemyCards: [
      { id: '69cd6d06da15bce3f890b819', title: 'Take Aim' },
      { id: '69cd6d62da15bce3f890b81b', title: 'Deadeye Shot' },
      { id: '69cd6d99da15bce3f890b81d', title: 'Feint' },
      { id: '69cd6e01da15bce3f890b81f', title: 'Gutting Strike' },
      { id: '69cd6e52da15bce3f890b821', title: 'Smoke Bomb' },
    ],
  },

  {
    id: 'lamp_keeper',
    name: 'Lamp Keeper',
    hp: 320,
    sp: 0,
    armor: 0,
    spawnWeight: 1,
    enemyCards: [
      { id: '69cf9e283ca70b3e99a39171', title: 'Lantern Staff' },
      { id: '69cf9e283ca70b3e99a39171', title: 'Lantern Staff' },
      { id: '69cf9d703ca70b3e99a3916d', title: 'Lower the Hood' },
      { id: '69cf9d703ca70b3e99a3916d', title: 'Lower the Hood' },
      { id: '69cf9dce3ca70b3e99a3916f', title: 'Open the Shutters' },
      { id: '69cf9dce3ca70b3e99a3916f', title: 'Open the Shutters' },
      { id: '69cf9e783ca70b3e99a39173', title: 'Trim the Wick' },
      { id: '69cf9ebd3ca70b3e99a39175', title: 'Soot Burst' },
      { id: '69cf9f053ca70b3e99a39177', title: 'Glass Flare' },
      { id: '69cf9f673ca70b3e99a39179', title: 'Keeper’s Circuit' },
    ],
  },



{
  id: 'custodian',
  name: 'Custodian',
  hp: 320,
  sp: 0,
  armor: 20,
  spawnWeight: 1,
  enemyCards: [
    { id: '69d0cbaa7da476a5e1d7b111', title: 'Forfeiture' },
    { id: '69d0cbd87da476a5e1d7b114', title: 'Inventory Tax' },
    { id: '69d0cc177da476a5e1d7b117', title: 'Reissued Violence' },
    { id: '69d0cc3f7da476a5e1d7b11a', title: 'Salvaged Ward' },
    { id: '69d0cc837da476a5e1d7b11d', title: 'Reprocessed Essence' },
    { id: '69d0ccb77da476a5e1d7b120', title: 'Full Processing' },
  ],
  allowEnemyLibraryFallbackWhenEmpty: false,
},

  {
    id: 'threshold_verifier',
    name: 'Threshold Verifier',
    hp: 350,
    sp: 0,
    armor: 50,
    spawnWeight: 1,
    enemyCards: [
    { id: '69d06816a64b4a0c23896b63', title: 'Threshold Check' },
    { id: '69d06681a64b4a0c23896b5e', title: 'Punitive Measure' },
    { id: '69d092f52dfd495dc731a406', title: 'Unauthorized Passage' },
    { id: '69d062dda64b4a0c23896b59', title: 'Counterweight' },
    { id: '69d0624ba64b4a0c23896b56', title: 'Deferred Sanction' },
  ],
    allowEnemyLibraryFallbackWhenEmpty: false,
  },

  {
    id: 'yahaka_voidfeeder_1',
    name: 'Yahaka, the Voidfeeder',
    hp: 100,
    sp: 0,
    armor: 0,
    spawnWeight: 0,
    enemyCards: [...YAHAKA_VARIANT_1_ENEMY_CARDS],
  },
  {
    id: 'yahaka_voidfeeder_2',
    name: 'Yahaka, the Voidfeeder',
    hp: 200,
    sp: 0,
    armor: 30,
    spawnWeight: 0,
    enemyCards: [...YAHAKA_VARIANT_2_ENEMY_CARDS],
  },
  {
    id: 'yahaka_voidfeeder_3',
    name: 'Yahaka, the Voidfeeder',
    hp: 270,
    sp: 0,
    armor: 50,
    spawnWeight: 0,
    enemyCards: [...YAHAKA_VARIANT_3_ENEMY_CARDS],
  },
  {
    id: 'yahaka_voidfeeder_4',
    name: 'Yahaka, the Voidfeeder',
    hp: 360,
    sp: 0,
    armor: 90,
    spawnWeight: 0,
    enemyCards: [...YAHAKA_VARIANT_4_ENEMY_CARDS],
  },
  {
    id: 'yahaka_voidfeeder_5',
    name: 'Yahaka, the Voidfeeder',
    hp: 420,
    sp: 0,
    armor: 100,
    spawnWeight: 0,
    enemyCards: [...YAHAKA_VARIANT_5_ENEMY_CARDS],
  },
  {
    id: 'yahaka_voidfeeder_6',
    name: 'Yahaka, the Voidfeeder',
    hp: 540,
    sp: 0,
    armor: 120,
    spawnWeight: 0,
    enemyCards: [...YAHAKA_VARIANT_6_ENEMY_CARDS],
  },
  {
    id: 'yahaka_voidfeeder_7',
    name: 'Yahaka, the Voidfeeder',
    hp: 690,
    sp: 0,
    armor: 200,
    spawnWeight: 0,
    enemyCards: [...YAHAKA_VARIANT_7_ENEMY_CARDS],
  },
  {
    id: 'nested_horror',
    name: 'Nested Horror',
    hp: 480,
    sp: 0,
    armor: 60,
    // Spawn weight controls how often this enemy is picked by `pickRandomEnemyId()`.
    // Set this to 1 to make it spawn like normal roamers (Cultist / Hexbinder).
    // If you later want it to be rarer, lower this (e.g. 0.25).
    spawnWeight: 0,
    // (Repeat an ID multiple times if you want multiple copies to increase its odds.)
    enemyCards: [
      // Core attacks
      { id: '69add3c6e3b805d4a0daf6f2', title: 'Crushing Bite' },

      // Utility / control
      { id: '69add4c8e3b805d4a0daf6f5', title: 'Paralyzing Venom' },
      { id: '69add37be3b805d4a0daf6ef', title: 'Acidic Silk' },
      { id: '69add2a4e3b805d4a0daf6e9', title: 'Web Cocoon' },
    ],
  },
{
    id: 'dreadbrand',
    name: 'Dreadbrand',
    hp: 200,
    sp: 0,
    armor: 80,
    spawnWeight: 1,
    enemyCards: [
      // Core attacks
      { id: '6998d63d0bebb02c4299bfe5', title: 'Dark Edict' },
      { id: '6998d68b0bebb02c4299bfe8', title: 'Iron Rend' },
      { id: '6998d4270bebb02c4299bfcc', title: 'Defensive Stance' },
      { id: '6998d4550bebb02c4299bfce', title: 'Frontal Slash' },
      { id: '6998d4cc0bebb02c4299bfd2', title: 'Sprint' },
    ],
  },
  {
    id: 'dark-apostle',
    name: 'Dark Apostle',
    hp: 333,
    sp: 0,
    armor: 0,
    spawnWeight: 1,
    enemyCards: [
      { id: '69c0b0039fabec631022ff52', title: 'Profane Restoration' },
      { id: '69bf74bf1247f91a7d102567', title: 'Soul Drain' },
      { id: '69bf73c51247f91a7d10255e', title: 'Cursed Fate' },
      { id: '69bf70b41247f91a7d102556', title: 'Punish' },
    ],
    allowEnemyLibraryFallbackWhenEmpty: false,
  },
];

function normalizeEnemyKey(input: unknown): string {
  // Normalize IDs and names so the combat layer can accept:
  // - "nested_horror" (id)
  // - "nested-horror" / "nested horror" (common variants)
  // - "Nested Horror" (display name)
  return String(input ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

export function findEnemyDef(idOrName: unknown): EnemyDef | null {
  if (!ENEMIES.length) return null;
  const key = normalizeEnemyKey(idOrName);
  if (!key) return null;

  // First: match by normalized ID.
  const byId = ENEMIES.find((e) => normalizeEnemyKey(e.id) === key);
  if (byId) return byId;

  // Second: match by normalized display name.
  const byName = ENEMIES.find((e) => normalizeEnemyKey(e.name) === key);
  if (byName) return byName;

  return null;
}

export function resolveEnemyId(idOrName: unknown): string {
  const found = findEnemyDef(idOrName);
  if (found) return found.id;
  // Finally: fall back to the first enemy.
  return ENEMIES.length ? ENEMIES[0].id : 'cultist';
}

export function getEnemyDef(idOrName: string): EnemyDef {
  const found = findEnemyDef(idOrName) ?? ENEMIES[0];
  return applyRuntimeEnemyOverride(found, runtimeEnemyOverrides.get(found.id));
}

export function enemyFighterFromDef(def: EnemyDef): Fighter {
  const hp = Math.max(1, Math.floor(def.hp));
  return {
    name: def.name,
    hp,
    maxHp: hp,
    sp: Math.max(0, Math.floor(def.sp)),
    armor: Math.max(0, Math.floor(def.armor)),
    stunned: 0,
    reflectPct: 0,
    negateNextDamage: 0,
  };
}

export function pickRandomEnemyId(): string {
  const pool = ENEMIES;
  if (!pool.length) return 'cultist';

  const w = (e: EnemyDef) => {
    const n = Number(e.spawnWeight ?? 1);
    return Number.isFinite(n) ? Math.max(0, n) : 1;
  };

  const total = pool.reduce((sum, e) => sum + w(e), 0);
  if (total <= 0) return pool[0].id;

  let r = Math.random() * total;
  for (const e of pool) {
    r -= w(e);
    if (r <= 0) return e.id;
  }
  return pool[pool.length - 1].id;
}
